// O resumo de uma métrica de saúde — média, tendência e idade, puro.
//
// ═════════════════════════════════════════════════════════════════════════
// ⚠️ ESTE ARQUIVO EXISTE PARA NÃO REPETIR O DEFEITO DO SONO
// ═════════════════════════════════════════════════════════════════════════
// A tela de métricas mostrava o ÚLTIMO valor de cada tipo em destaque — e
// `latestOf` pega a primeira linha de uma janela de NOVENTA dias, então com o
// sync parado ela exibiu passos, FC e HRV de onze dias atrás como se fossem de
// hoje (issue #480). O conserto de lá foi mostrar a idade, e `metricAge` já
// faz isso.
//
// O conserto que faltava é de HIERARQUIA, e é o mesmo do módulo de sono: o
// número de hoje é ruído; a cadência oficial do VYV é 7 e 30 dias. Se este
// painel nascesse com o número grande do dia e a tendência escondida, nasceria
// com o problema que o outro item deste lote existe para consertar.
//
// PURO: sem React, sem Supabase, `now` por parâmetro.

import { metricAge, type MetricAge } from "./metricAge";

export interface MetricRow {
  metric_type: string;
  value: number | null;
  recorded_at: string;
}

/** Para onde a métrica anda — sem julgar se é bom (isso depende do tipo). */
export type Direction = "up" | "down" | "flat";

export interface MetricSummary {
  type: string;
  /** Última leitura da janela. Pode ser velha — por isso `age` vem junto. */
  latest: number | null;
  age: MetricAge | null;
  avg7: number | null;
  avg30: number | null;
  /** Média dos 7 dias ANTERIORES aos 7 atuais. */
  avgPrev7: number | null;
  deltaPct: number | null;
  direction: Direction;
  /** Leituras que entraram em avg7 — o denominador. */
  n7: number;
  n30: number;
}

/** Abaixo disto, "tendência" é uma leitura solta com nome bonito. */
export const MIN_POINTS_FOR_TREND = 3;

/** Variação, em %, a partir da qual a direção deixa de ser "estável". */
export const FLAT_BAND_PCT = 3;

function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

function round1(v: number | null): number | null {
  return v === null ? null : Math.round(v * 10) / 10;
}

/**
 * Resume um tipo de métrica.
 *
 * As janelas são móveis (últimos N dias a partir de `now`) e não de calendário:
 * "os últimos 7 dias" é o que a pessoa pergunta olhando o próprio corpo, e é o
 * mesmo recorte que as views de adoção do portal usam para 7d/30d.
 */
export function metricSummary(rows: MetricRow[], type: string, now: number): MetricSummary {
  const dayMs = 86_400_000;
  const pts = rows
    .filter((r) => r.metric_type === type && r.value !== null)
    .map((r) => ({ t: Date.parse(r.recorded_at), v: r.value as number }))
    .filter((p) => Number.isFinite(p.t) && p.t <= now && Number.isFinite(p.v) && p.v >= 0)
    .sort((a, b) => b.t - a.t);

  const inRange = (from: number, to: number) =>
    pts.filter((p) => p.t >= from && p.t <= to).map((p) => p.v);

  const w7 = inRange(now - 7 * dayMs, now);
  const w30 = inRange(now - 30 * dayMs, now);
  // Half-open previous window: the 7-day boundary belongs only to the current week.
  const wPrev7 = pts.filter(p => p.t >= now - 14 * dayMs && p.t < now - 7 * dayMs).map(p => p.v);

  const avg7 = round1(mean(w7));
  const avgPrev7 = round1(mean(wPrev7));

  // Tendência só existe com amostra dos DOIS lados. Sem isso, `direction` é
  // "flat" e `deltaPct` é null — e null não é 0%: um diz "não sei", o outro
  // diz "não mudou".
  const comparable =
    avg7 !== null && avgPrev7 !== null && avgPrev7 !== 0 &&
    w7.length >= MIN_POINTS_FOR_TREND && wPrev7.length >= MIN_POINTS_FOR_TREND;

  const deltaPct = comparable ? Math.round(((avg7! - avgPrev7!) / avgPrev7!) * 1000) / 10 : null;
  const direction: Direction =
    deltaPct === null || Math.abs(deltaPct) < FLAT_BAND_PCT ? "flat" : deltaPct > 0 ? "up" : "down";

  const newest = pts[0];
  return {
    type,
    latest: newest ? newest.v : null,
    age: newest ? metricAge(new Date(newest.t).toISOString(), now) : null,
    avg7,
    avg30: round1(mean(w30)),
    avgPrev7,
    deltaPct,
    direction,
    n7: w7.length,
    n30: w30.length,
  };
}

/**
 * A chave i18n da leitura — "o que fazer", não número seco.
 *
 * ⚠️ A DIREÇÃO NÃO DIZ SE É BOM: HRV subindo é bom, frequência cardíaca em
 * repouso subindo não é, passos subindo é. Por isso a chave é POR TIPO e por
 * direção, e o julgamento vive no texto traduzido — não numa cor genérica de
 * seta que mentiria para uma das três.
 *
 * ⛔ Dado velho manda em tudo: sem sync recente, a leitura certa é sobre o
 * SYNC, não sobre o corpo da pessoa.
 */
export function readingKeyFor(s: MetricSummary): string {
  if (s.age?.stale) return "health.reading.stale";
  if (s.deltaPct === null) return "health.reading.needMore";
  return `health.reading.${s.type}.${s.direction}`;
}
